import React, { useState } from "react";
import { X, Upload, Loader, CheckCircle, AlertCircle, Database, Eye, DollarSign } from "lucide-react";
import { api } from "../../services/api";

const COUNTRIES = [
  { code: "mk", name: "Macedonia", currency: "MKD", flag: "🇲🇰" },
  { code: "rs", name: "Serbia", currency: "RSD", flag: "🇷🇸" },
  { code: "bg", name: "Bulgaria", currency: "BGN", flag: "🇧🇬" },
  { code: "gr", name: "Greece", currency: "EUR", flag: "🇬🇷" },
  { code: "al", name: "Albania", currency: "ALL", flag: "🇦🇱" },
  { code: "xk", name: "Kosovo", currency: "EUR", flag: "🇽🇰" },
];

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: () => void;
}

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
}) => {
  const [bulkImportText, setBulkImportText] = useState("");
  const [baseImageUrl, setBaseImageUrl] = useState("https://naturatherapy.mk/image/cache/");
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);

  const normalizeImageUrl = (imagePath: string, baseUrl: string): string => {
    if (!imagePath) return "";
    
    // If already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    
    // If base URL is empty or not provided, use image path as-is (assume it's a full link)
    if (!baseUrl || baseUrl.trim() === "") {
      return imagePath;
    }
    
    // If base URL is provided, prepend it
    const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    // Remove leading slash from image path if present
    const path = imagePath.startsWith("/") ? imagePath.slice(1) : imagePath;
    // Remove 'catalog/' prefix if present (already handled by base URL)
    const cleanPath = path.replace(/^catalog\//, "");
    return `${base}${cleanPath}`;
  };

  // Parse product name to detect opportunities and extract base name
  interface ParsedProduct {
    baseName: string;
    opportunity?: {
      buyQuantity: number;
      getQuantity: number;
      variantSuffix: string;
      giftItems?: Array<{ productName: string; quantity: number }>;
    };
  }

  const parseProductName = (productName: string): ParsedProduct => {
    let baseName = productName.trim();
    let opportunity: ParsedProduct['opportunity'] | undefined = undefined;

    // Pattern 1: "Product (X+Y)" or "Product (X+1)"
    const pattern1 = /^(.+?)\s*\((\d+)\+(\d+)\)/i;
    const match1 = productName.match(pattern1);
    if (match1) {
      baseName = match1[1].trim();
      const buyQty = parseInt(match1[2]);
      const getQty = parseInt(match1[3]);
      
      // Check if there's additional info in parentheses after (X+Y)
      const remainingAfterPattern = productName.substring(match1[0].length).trim();
      let variantSuffix = `(${buyQty}+${getQty})`;
      
      // Check for additional parentheses with variant details
      const additionalPattern = /^\s*\(([^)]+)\)/;
      const additionalMatch = remainingAfterPattern.match(additionalPattern);
      if (additionalMatch) {
        variantSuffix += ` ${additionalMatch[0]}`;
      }
      
      // Check for "+ gift" or "+ FREE X"
      let giftItems: Array<{ productName: string; quantity: number }> | undefined = undefined;
      const giftPattern = /(?:\+\s*gift|\+\s*FREE\s+([A-Za-z0-9\s]+))/i;
      const giftMatch = productName.match(giftPattern);
      if (giftMatch) {
        const giftName = giftMatch[1]?.trim() || "Gift";
        variantSuffix += ` + gift ${giftName}`;
        giftItems = [{ productName: giftName, quantity: 1 }];
      }
      
      opportunity = {
        buyQuantity: buyQty,
        getQuantity: getQty,
        variantSuffix,
        giftItems,
      };
      
      return { baseName, opportunity };
    }

    // Pattern 2: "Xx PRODUCT + FREE Y" or "Xx PRODUCT + gift"
    const pattern2 = /^(\d+)x\s+(.+?)(?:\s*\+\s*(?:FREE\s+)?([A-Za-z0-9\s]+)|(?:\s*\+\s*gift))?$/i;
    const match2 = productName.match(pattern2);
    if (match2) {
      const buyQty = parseInt(match2[1]);
      baseName = match2[2].trim();
      const giftName = match2[3]?.trim();
      
      let variantSuffix = `(${buyQty}+1)`;
      let giftItems: Array<{ productName: string; quantity: number }> | undefined = undefined;
      
      if (giftName) {
        variantSuffix += ` + gift ${giftName}`;
        giftItems = [{ productName: giftName, quantity: 1 }];
      } else if (productName.toLowerCase().includes('+ gift')) {
        variantSuffix += ' + gift';
      }
      
      opportunity = {
        buyQuantity: buyQty,
        getQuantity: 1,
        variantSuffix,
        giftItems,
      };
      
      return { baseName, opportunity };
    }

    // Pattern 3: "Product (X+Y) + gift" (already covered but check standalone)
    const pattern3 = /^(.+?)\s*\((\d+)\+(\d+)\)\s*\+\s*gift/i;
    const match3 = productName.match(pattern3);
    if (match3) {
      baseName = match3[1].trim();
      opportunity = {
        buyQuantity: parseInt(match3[2]),
        getQuantity: parseInt(match3[3]),
        variantSuffix: `(${match3[2]}+${match3[3]}) + gift`,
        giftItems: [{ productName: "Gift", quantity: 1 }],
      };
      return { baseName, opportunity };
    }

    return { baseName };
  };

  const parseBulkImportData = (text: string, baseImageUrl: string = "") => {
    const lines = text
      .trim()
      .split("\n")
      .filter((line) => line.trim() !== "");

    if (lines.length === 0) {
      throw new Error("No data provided");
    }

    // First line might be headers - skip if it contains common header keywords
    const headerKeywords = ["product_id", "product_name", "model", "sku"];
    const firstLineLower = lines[0].toLowerCase();
    const hasHeaders = headerKeywords.some((keyword) =>
      firstLineLower.includes(keyword)
    );
    const dataLines = hasHeaders ? lines.slice(1) : lines;

    const productsData: any[] = [];

    dataLines.forEach((line, index) => {
      // Support both tab and comma separation
      const separator = line.includes("\t") ? "\t" : ",";
      const parts = line.split(separator).map((p) => p.trim());

      if (parts.length < 2) {
        console.warn(`⚠️ Line ${index + 1}: Invalid format, skipping`);
        return;
      }

      // Expected columns:
      // product_id, product_name, model, sku, upc, ean, jan, isbn, mpn, location, quantity, stock_status, main_image, manufacturer_name, price_mrp, tax_class_id, date_available, weight, weight (duplicate), date_added, date_modified, category_names, additional_images, attributes

      const [
        product_id,
        product_name,
        model = "",
        sku = "",
        upc = "",
        ean = "",
        jan = "",
        isbn = "",
        mpn = "",
        location = "",
        quantity = "0",
        stock_status = "out_of_stock",
        main_image = "",
        manufacturer_name = "",
        price_mrp = "0",
        tax_class_id = "0",
        date_available = "",
        weight = "0",
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _weight2 = "", // Handle duplicate weight column (ignored)
        date_added = "",
        date_modified = "",
        category_names = "",
        additional_images = "",
        attributes = "",
      ] = parts;

      if (!product_name) {
        console.warn(`⚠️ Line ${index + 1}: Missing product name, skipping`);
        return;
      }

      // Parse product name for opportunities and extract base name
      const parsed = parseProductName(product_name);
      let finalProductName = parsed.baseName;
      let extractedDescription = "";
      
      // Also handle dash-based description extraction if no opportunity pattern found
      if (!parsed.opportunity && product_name.includes("-")) {
        const parts = product_name.split("-");
        if (parts.length > 1) {
          // Keep only the part before the first dash as product name
          finalProductName = parts[0].trim();
          // Take everything after the first dash and capitalize first letter
          const descPart = parts.slice(1).join("-").trim();
          if (descPart) {
            extractedDescription = descPart.charAt(0).toUpperCase() + descPart.slice(1).toLowerCase();
          }
        }
      }

      // Normalize image URLs
      const normalizedMainImage = normalizeImageUrl(main_image, baseImageUrl);
      const normalizedAdditionalImages = additional_images
        ? additional_images.split(",").map(img => normalizeImageUrl(img.trim(), baseImageUrl)).join(", ")
        : "";

      const qty = parseInt(quantity) || 0;
      // Auto-set stock_status based on quantity
      const autoStockStatus = qty > 0 ? "in_stock" : "out_of_stock";
      const finalStockStatus = stock_status && stock_status !== "out_of_stock" ? stock_status : autoStockStatus;

      productsData.push({
        product_id: product_id || null,
        product_name: finalProductName, // Use the split product name (without description part)
        model,
        sku,
        upc,
        ean,
        jan,
        isbn,
        mpn,
        location,
        quantity: qty,
        stock_status: finalStockStatus,
        main_image: normalizedMainImage,
        manufacturer_name: manufacturer_name || null,
        price_mkd: parseFloat(price_mrp) || 0, // Map price_mrp to price_mkd
        price_mrp: parseFloat(price_mrp) || 0, // Also keep price_mrp
        tax_class_id: parseInt(tax_class_id) || 0,
        date_available: date_available || null,
        weight: parseFloat(weight) || 0,
        date_added: date_added || null,
        date_modified: date_modified || null,
        category_names,
        additional_images: normalizedAdditionalImages,
        attributes,
        _extractedDescription: extractedDescription, // Temporary field for description_en in translations
        _parsedOpportunity: parsed.opportunity, // Store opportunity data for later processing
        _originalProductName: product_name, // Keep original for reference
      });
    });

    return productsData;
  };

  const handlePreview = async () => {
    if (!bulkImportText.trim()) {
      setError("Please paste product data");
      return;
    }

    setIsLoadingPrices(true);
    setError(null);

    try {
      const productsData = parseBulkImportData(bulkImportText, baseImageUrl);
      console.log(`📦 [BULK-IMPORT] Parsed ${productsData.length} products for preview`);

      // Fetch prices from orders
      const productNames = productsData.map(p => p.product_name).filter(Boolean);
      if (productNames.length > 0) {
        try {
          const priceResponse = await api.matchPricesFromOrders(productNames);
          if (priceResponse.success && priceResponse.data?.priceMap) {
            // Enrich products with matched prices
            productsData.forEach(product => {
              const matchedPrices = priceResponse.data.priceMap[product.product_name];
              if (matchedPrices) {
                product.matchedPrices = matchedPrices;
              }
            });
          }
        } catch (priceErr) {
          console.warn("⚠️ [BULK-IMPORT] Could not fetch prices from orders:", priceErr);
        }
      }

      setPreviewData(productsData);
      setShowPreview(true);
    } catch (err: any) {
      console.error("❌ [BULK-IMPORT] Error:", err);
      setError(err.message || "Failed to parse products");
    } finally {
      setIsLoadingPrices(false);
    }
  };

  const handleBulkImport = async () => {
    setIsImporting(true);
    setError(null);
    setImportResults(null);

    try {
      // Use preview data if available, otherwise parse again
      const productsData = previewData.length > 0 ? previewData : parseBulkImportData(bulkImportText, baseImageUrl);
      
      // Separate base products and variant products (with opportunities)
      const baseProducts: any[] = [];
      const variantProducts: any[] = [];
      
      productsData.forEach(product => {
        if ((product as any)._parsedOpportunity) {
          variantProducts.push(product);
        } else {
          baseProducts.push(product);
        }
      });

      // Prepare base products with country prices from matched prices
      const enrichedProducts = baseProducts.map(product => {
        const productData: any = { ...product };
        
        // Add country prices if matched
        if (product.matchedPrices) {
          productData.countryPrices = {};
          Object.entries(product.matchedPrices).forEach(([country, priceInfo]: [string, any]) => {
            productData.countryPrices[country] = {
              price: priceInfo.price,
              currency: priceInfo.currency,
              updatedAt: new Date()
            };
          });
        }
        
        // Add extracted description to base description field and as description_en in translations
        if ((product as any)._extractedDescription) {
          // Save to base description field
          productData.description = (product as any)._extractedDescription;
          
          if (!productData.translations) {
            productData.translations = {};
          }
          
          // Add description_en to all country translations
          COUNTRIES.forEach(country => {
            if (!productData.translations[country.code]) {
              productData.translations[country.code] = {};
            }
            productData.translations[country.code].description_en = (product as any)._extractedDescription;
            productData.translations[country.code].updatedAt = new Date();
          });
          
          // Remove temporary field
          delete (productData as any)._extractedDescription;
        }
        
        // Remove temporary fields
        delete productData.matchedPrices;
        delete (productData as any)._parsedOpportunity;
        delete (productData as any)._originalProductName;
        
        return productData;
      });

      // Import base products first
      const baseImportResponse = await api.bulkImportProducts(enrichedProducts);
      
      if (!baseImportResponse.success) {
        throw new Error("Failed to import base products");
      }

      // Now create opportunities for variant products
      let opportunitiesCreated = 0;
      const createdProductMap = new Map<string, string>(); // Map original product name to new product ID

      // Get all imported products to find base products
      const allProductsResponse = await api.getProducts({ limit: 10000 });
      if (allProductsResponse.success && allProductsResponse.data?.products) {
        allProductsResponse.data.products.forEach((p: any) => {
          createdProductMap.set(p.product_name, p._id || p.product_id);
        });
      }

      // Process variant products - create base product if needed, then create opportunity
      for (const variantProduct of variantProducts) {
        try {
          const opportunity = (variantProduct as any)._parsedOpportunity;
          const baseProductName = variantProduct.product_name;
          
          // Find or create base product
          let baseProductId = createdProductMap.get(baseProductName);
          
          if (!baseProductId) {
            // Check if base product exists in database
            const searchResponse = await api.getProducts({ 
              search: baseProductName,
              limit: 10 
            });
            
            if (searchResponse.success && searchResponse.data?.products?.length > 0) {
              // Find exact match
              const exactMatch = searchResponse.data.products.find(
                (p: any) => p.product_name.toLowerCase() === baseProductName.toLowerCase()
              );
              if (exactMatch) {
                baseProductId = exactMatch._id || exactMatch.product_id;
              }
            }
          }

          // If base product doesn't exist, create it
          if (!baseProductId) {
            const baseProductData = {
              ...variantProduct,
              product_name: baseProductName,
              price_mkd: variantProduct.price_mrp || variantProduct.price_mkd || 0,
            };
            
            // Remove temporary fields
            delete (baseProductData as any)._extractedDescription;
            delete (baseProductData as any)._parsedOpportunity;
            delete (baseProductData as any)._originalProductName;
            delete baseProductData.matchedPrices;
            
            // Add country prices if matched
            if (variantProduct.matchedPrices) {
              baseProductData.countryPrices = {};
              Object.entries(variantProduct.matchedPrices).forEach(([country, priceInfo]: [string, any]) => {
                baseProductData.countryPrices[country] = {
                  price: priceInfo.price,
                  currency: priceInfo.currency,
                  updatedAt: new Date()
                };
              });
            }
            
            const createResponse = await api.createProduct(baseProductData);
            if (createResponse.success && createResponse.data?.product) {
              baseProductId = createResponse.data.product._id || createResponse.data.product.product_id || "";
              if (baseProductId) {
                createdProductMap.set(baseProductName, baseProductId);
              }
            } else {
              console.warn(`⚠️ Failed to create base product: ${baseProductName}`);
              continue;
            }
          }

          // Create opportunity
          const opportunityData = {
            baseProductId: baseProductId,
            baseProductName: baseProductName,
            opportunityType: "buy_x_get_y" as const,
            name: `${baseProductName} ${opportunity.variantSuffix}`,
            description: `Auto-generated from bulk import: ${variantProduct._originalProductName || variantProduct.product_name}`,
            buyQuantity: opportunity.buyQuantity,
            getQuantity: opportunity.getQuantity,
            giftItems: opportunity.giftItems,
            variantSuffix: opportunity.variantSuffix,
            variantSKU: variantProduct.sku ? `${variantProduct.sku}-VAR` : undefined,
            fixedPrice: variantProduct.price_mrp || variantProduct.price_mkd || undefined,
            enabled: true,
          };

          const oppResponse = await api.createOpportunity(opportunityData);
          if (oppResponse.success) {
            opportunitiesCreated++;
            console.log(`✅ Created opportunity: ${opportunityData.name}`);
          } else {
            console.warn(`⚠️ Failed to create opportunity: ${opportunityData.name}`);
          }
        } catch (err: any) {
          console.error(`❌ Error processing variant product: ${err.message}`);
        }
      }

      // Import already done above, now set results
      const totalImported = baseImportResponse.data?.imported || 0;
      
      setImportResults({
        success: true,
        imported: totalImported,
        total: productsData.length,
        opportunitiesCreated,
        baseProductsCount: enrichedProducts.length,
        variantProductsCount: variantProducts.length,
        message: `Imported ${totalImported} base products and created ${opportunitiesCreated} opportunities`,
      });
      
      setBulkImportText("");
      setPreviewData([]);
      setShowPreview(false);
      onImport(); // Refresh products list
    } catch (err: any) {
      console.error("❌ [BULK-IMPORT] Error:", err);
      setError(err.message || "Failed to import products");
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ marginTop: 0 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Database className="w-5 h-5 mr-2 text-[#264a3e]" />
            Bulk Import Products
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
              <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {importResults && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center mb-2">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <h4 className="text-sm font-semibold text-green-900">
                  Import Completed
                </h4>
              </div>
              <div className="text-sm text-green-800">
                <p>
                  Successfully imported <strong>{importResults.imported}</strong> base products
                  {importResults.opportunitiesCreated > 0 && (
                    <> and created <strong>{importResults.opportunitiesCreated}</strong> opportunities</>
                  )}
                  <br />
                  <span className="text-sm text-gray-600 mt-1 block">
                    Base products: {importResults.baseProductsCount || 0} | 
                    Variant products: {importResults.variantProductsCount || 0}
                  </span>
                </p>
                {importResults.errors && importResults.errors.length > 0 && (
                  <p className="mt-2 text-red-700">
                    {importResults.errors.length} errors occurred
                  </p>
                )}
              </div>
            </div>
          )}

          {showPreview ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-[#264a3e]" />
                  Preview ({previewData.length} products)
                </h4>
                <button
                  onClick={() => {
                    setShowPreview(false);
                    setPreviewData([]);
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Edit Data
                </button>
              </div>

              <div className="max-h-[50vh] overflow-y-auto space-y-4 mb-4">
                {previewData.map((product, index) => {
                  const matchedPrices = product.matchedPrices || {};
                  const hasMatchedPrices = Object.keys(matchedPrices).length > 0;

                  return (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900">{product.product_name}</h5>
                          {product.description && (
                            <p className="text-sm text-gray-600 mt-1 italic">
                              {product.description}
                            </p>
                          )}
                          <div className="text-sm text-gray-600 mt-1">
                            <span>SKU: {product.sku || "—"}</span>
                            {product.model && <span className="ml-3">Model: {product.model}</span>}
                            <span className={`ml-3 px-2 py-1 rounded text-xs ${
                              product.stock_status === "in_stock" 
                                ? "bg-green-100 text-green-800" 
                                : "bg-red-100 text-red-800"
                            }`}>
                              {product.quantity} - {product.stock_status === "in_stock" ? "In Stock" : "Out of Stock"}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {product.price_mkd?.toFixed(2) || "0.00"} MKD
                          </div>
                        </div>
                      </div>

                      {hasMatchedPrices && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center mb-2">
                            <DollarSign className="w-4 h-4 mr-1 text-green-600" />
                            <span className="text-xs font-semibold text-gray-700">Prices from Orders:</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {COUNTRIES.map(country => {
                              const priceInfo = matchedPrices[country.code];
                              if (!priceInfo) return null;
                              return (
                                <div
                                  key={country.code}
                                  className="flex items-center space-x-1 px-2 py-1 bg-white rounded border border-green-200"
                                >
                                  <span>{country.flag}</span>
                                  <span className="text-xs font-medium text-gray-700">
                                    {priceInfo.price.toFixed(2)} {priceInfo.currency}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowPreview(false);
                    setPreviewData([]);
                  }}
                  disabled={isImporting}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={handleBulkImport}
                  disabled={isImporting}
                  className="px-4 py-2 bg-[#264a3e] text-white rounded-md hover:bg-[#1e3a32] disabled:opacity-50 flex items-center"
                >
                  {isImporting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Products
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Base Image URL
              </label>
              <input
                type="text"
                value={baseImageUrl}
                onChange={(e) => setBaseImageUrl(e.target.value)}
                placeholder="https://naturatherapy.mk/image/cache/"
                disabled={isImporting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500">
                Base URL for product images. Relative image paths from the import data will be prefixed with this URL.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Paste Tab or Comma-Separated Data from Excel
              </label>
              <textarea
                value={bulkImportText}
                onChange={(e) => setBulkImportText(e.target.value)}
                placeholder={`product_id	product_name	model	sku	upc	ean	jan	isbn	mpn	location	quantity	stock_status	main_image	manufacturer_name	price_mkd	tax_class_id	date_available	weight	date_added	date_modified	category_names	additional_images	attributes
199792	Aloe Vera with Aronija 1l	ALOE	199792	...`}
                rows={15}
                disabled={isImporting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] font-mono text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <div className="mt-2 text-xs text-gray-500 space-y-1">
                <div>
                  <strong>Expected columns:</strong> product_id, product_name, model, sku, upc, ean, jan, isbn, mpn, location, quantity, stock_status, main_image, manufacturer_name, price_mrp, tax_class_id, date_available, weight, date_added, date_modified, category_names, additional_images, attributes
                </div>
                <div>
                  • <strong>Note:</strong> The column <strong>price_mrp</strong> will be mapped to both price_mrp and price_mkd fields
                </div>
                <div>
                  • Supports both <strong>Tab</strong> and <strong>Comma</strong> separated values
                </div>
                <div>
                  • First row can be headers (will be automatically skipped)
                </div>
                <div>
                  • <strong>Required:</strong> product_name
                </div>
                <div>
                  • <strong>Optional:</strong> All other fields (will use defaults if empty)
                </div>
                <div>
                  • <strong>Image URLs:</strong> Relative paths in main_image/additional_images will be prefixed with the Base Image URL above. Full URLs (starting with http:// or https://) will be used as-is.
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={onClose}
                disabled={isImporting || isLoadingPrices}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Close
              </button>
              <button
                onClick={handlePreview}
                disabled={isLoadingPrices || !bulkImportText.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {isLoadingPrices ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Loading Prices...
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    Preview & Match Prices
                  </>
                )}
              </button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;

